import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'app/store';
import { Box } from '@chakra-ui/react';
import { Invite, InviteResponseType } from 'types/invite.types';
import { Notification } from 'types/notification.types';
import { Link } from 'types/api.types';
import { ReactComponent as NotificationIcon } from 'resources/images/notifications.svg';
import { ChakraEmptyList } from 'app/components/Core/ChakraEmptyList';
import {
  InvitesList,
  InvitesListProps,
} from 'app/components/Notifications/InvitesList';
import {
  NotificationsList,
  NotificationsListProps,
} from 'app/components/Notifications/NotificationsList';
import { resetLoadingStateAction } from 'app/store/Notifications/slice';
import { clickNotificationAction } from 'app/store/Mixpanel/slice';

export interface NotificationsComponentProps {
  notifications: Notification[];
  invites: Invite[];
  loading: boolean;
  moreInvitesLink?: Link;
  isLoadingMoreInvites: boolean;
  fetchMoreInvites: (moreInvitesLink: Link) => void;
  respondToInvite: (
    response: InviteResponseType,
    guid: string,
    inviterName: string,
    inviterGuid: string
  ) => void;
  markNotificationAsRead: (guid: string) => void;
  markNotificationAsOld: (guid: string) => void;
  InvitesListComponent?: (props: InvitesListProps) => JSX.Element;
  NotificationsListComponent?: (props: NotificationsListProps) => JSX.Element;
  EmptyStateComponent?: React.ReactNode;
  onRowClick?: () => void;
  resetLoadersOnUnmount?: boolean;
}

const compare = (prevProps, nextProps) => {
  return JSON.stringify(prevProps) === JSON.stringify(nextProps);
};

const MemoizedInvitesList = React.memo(InvitesList, compare);
const MemoizedNotificationsList = React.memo(NotificationsList, compare);

export const NotificationsComponent: React.FC<NotificationsComponentProps> = ({
  notifications,
  invites,
  loading,
  moreInvitesLink,
  isLoadingMoreInvites,
  respondToInvite,
  fetchMoreInvites,
  markNotificationAsRead,
  markNotificationAsOld,
  NotificationsListComponent: NotificationsListComponentProp,
  InvitesListComponent: InvitesListComponentProp,
  EmptyStateComponent,
  onRowClick,
  resetLoadersOnUnmount = true,
}) => {
  const dispatch = useAppDispatch();
  const [newestNotificationGuid, setNewestNotificationGuid] = useState('');
  const [newestInviteGuid, setNewestInviteGuid] = useState('');
  const newestNotificationIndex = notifications.findIndex(
    (notif) => notif.guid === newestNotificationGuid
  );
  const newestInviteIndex = invites.findIndex(
    (invite) => invite.guid === newestInviteGuid
  );
  useEffect(
    () => () => {
      if (resetLoadersOnUnmount) {
        dispatch(resetLoadingStateAction());
      }
    },
    [dispatch, resetLoadersOnUnmount]
  );

  useEffect(() => {
    if (newestInviteIndex === -1 && invites.length > 0 && !loading) {
      setNewestInviteGuid(invites[0].guid);
    }
    if (
      newestNotificationIndex === -1 &&
      notifications.length > 0 &&
      !loading
    ) {
      setNewestNotificationGuid(notifications[0].guid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    newestNotificationIndex,
    newestInviteIndex,
    loading,
    notifications.length,
    invites.length,
  ]);

  const invitesSorted = useMemo(
    () =>
      invites
        .filter((inv, index) => index < newestInviteIndex)
        .concat(invites.filter((inv, index) => index >= newestInviteIndex)),
    [invites, newestInviteIndex]
  );

  const notificationsSorted = useMemo(
    () =>
      notifications
        .filter((n, index) => index < newestNotificationIndex)
        .concat(
          notifications.filter((n, index) => index >= newestNotificationIndex)
        ),
    [newestNotificationIndex, notifications]
  );

  const handleMixpanelOnNotificationClicked = (notification: Notification) => {
    dispatch(clickNotificationAction(notification.body));
  };

  const NotificationsListComponent =
    NotificationsListComponentProp || MemoizedNotificationsList;
  const InvitesListComponent = InvitesListComponentProp || MemoizedInvitesList;
  const showEmptyState = !loading && !notifications.length && !invites.length;

  return (
    <>
      {showEmptyState ? (
        <>
          {EmptyStateComponent || (
            <ChakraEmptyList
              icon={<NotificationIcon role="img" />}
              title="You don't have any notifications yet"
              primaryText="Don't worry - any new activity will be posted here. In the mean time, invite a friend to Session Studio, or start a new song."
            />
          )}
        </>
      ) : (
        <>
          {loading && <Box pt={14} />}
          <InvitesListComponent
            invites={invitesSorted}
            moreInvitesLink={moreInvitesLink}
            isLoadingMoreInvites={isLoadingMoreInvites}
            respondToInvite={respondToInvite}
            fetchMoreInvites={fetchMoreInvites}
            loading={loading}
          />
          {!loading && (
            <NotificationsListComponent
              notifications={notificationsSorted}
              markNotificationAsRead={markNotificationAsRead}
              markNotificationAsOld={markNotificationAsOld}
              onClick={(notification) => {
                handleMixpanelOnNotificationClicked(notification);
                if (onRowClick) {
                  onRowClick();
                }
              }}
            />
          )}
        </>
      )}
    </>
  );
};
